import React, { createContext, useContext, useEffect, useState } from 'react'
import { KeyBindings, DEFAULT_KEY_BINDINGS } from '../../constants/defaultKeyBindings'
import { keyBindingsService } from '../../services/keyBindingsService'
import KeyboardSettingsModal from '../../components/KeyboardSettingsModal'
import { isPrerendered } from '../../utils/env'

type KeyBindingsContextType = {
  keyBindings: KeyBindings
  setKeyBindings: (b: KeyBindings) => void
  openModal: () => void
}

const KeyBindingsContext = createContext<KeyBindingsContextType | null>(null)

export const KeyBindingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Pre-rendered HTML shows the default keys; hydrate with them, then apply saved ones
  const [keyBindings, setKeyBindingsState] = useState<KeyBindings>(
    () => isPrerendered ? { ...DEFAULT_KEY_BINDINGS } : keyBindingsService.load()
  )
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (isPrerendered) setKeyBindingsState(keyBindingsService.load())
  }, [])

  const setKeyBindings = (b: KeyBindings) => {
    setKeyBindingsState(b)
  }

  return (
    <KeyBindingsContext.Provider value={{ keyBindings, setKeyBindings, openModal: () => setModalOpen(true) }}>
      {children}
      {modalOpen && (
        <KeyboardSettingsModal
          bindings={keyBindings}
          onClose={() => setModalOpen(false)}
          onSave={(b) => { setKeyBindings(b) }}
        />
      )}
    </KeyBindingsContext.Provider>
  )
}

export function useKeyBindings(): KeyBindingsContextType {
  const ctx = useContext(KeyBindingsContext)
  if (!ctx) throw new Error('useKeyBindings must be used within KeyBindingsProvider')
  return ctx
}
